import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { Alert, Button, Skeleton, Tag, Tooltip } from 'antd'
import { CreateTicketsForGroupModal } from 'features/libra/dashboard/CreateTicketsForGroupModal'
import { DeleteTicketsForGroupModal } from 'features/libra/dashboard/DeleteTicketsForGroupModal'
import { LibraDashboardConfig } from 'features/libra/dashboard/libraDashboardApi'
import { LibraTicketDashboard } from 'features/libra/dashboard/ticketDashboard/libra/LibraTicketDashboard'
import { checkCreateVersionPermission } from 'features/libra/dashboard/ticketDashboard/libra/settings/createVersionPermission'
import { useToggleRoiVisible } from 'features/libra/dashboard/useToggleRoiVisible'
import {
  type LibraTicket,
  LibraTicketGroup,
  useDashboardLibraTickets,
  useLibraWorkflows,
} from 'features/libra/ticket/libraTicketsApi'
import {
  getLatestTicketPerAssetForStats,
  getLatestVersions,
} from 'features/libra/ticket/ticketVersioning'
import { PermissionsGate, useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import uniq from 'lodash/uniq'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  getLibraTicketGroupAdminPermission,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'

import { AssetStatusTracker } from './AssetStatusTracker'
import { ExecutiveDashboard } from './ExecutiveDashboard'
import { AssetStatus, getAssetStatus } from './getAssetStatus'
import { LegacyDashboardViews } from './LegacyDashboardViews'

export const LibraDashboardContent: FC<{
  config: LibraDashboardConfig
  group: LibraTicketGroup
  unitId?: string
  orgId?: string
  dashboardConfigId?: string
  onRefreshGroup: () => void
}> = ({ config, group, onRefreshGroup }) => {
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(
    undefined
  )
  const [selectedStatus, setSelectedStatus] = useState<AssetStatus | undefined>(
    undefined
  )

  const groupId = group.id
  const { hasPermissions } = useGeckoPermissions()
  const {
    isGeckoInternal,
    isOptedIn,
    saving: hidingRoi,
    setVisible: setRoiVisible,
  } = useToggleRoiVisible(config)
  const hideRoiForMe = useCallback(() => {
    void setRoiVisible(false)
  }, [setRoiVisible])

  const isAdmin = hasPermissions([getLibraTicketGroupAdminPermission(groupId)])
  const canCreateVersion = useCallback(() => isAdmin, [isAdmin])
  const createNewVersionRoles = config.ticketDashboard?.createNewVersion.roles
  const hasCreateVersionPermission = checkCreateVersionPermission(
    hasPermissions,
    groupId,
    createNewVersionRoles
  )

  const {
    tickets,
    isLoading: ticketsLoading,
    forceRefresh,
  } = useDashboardLibraTickets(
    useMemo(
      () => ({
        [RolodexEntryType.LIBRA_TICKET_GROUP]: [groupId],
      }),
      [groupId]
    )
  )

  const organizationId = config.tags?.organization as string | undefined
  const latestTickets = useMemo(() => getLatestVersions(tickets), [tickets])

  const workflowIds = useMemo(
    () =>
      uniq(
        latestTickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      ),
    [latestTickets]
  )

  const {
    libraWorkflows,
    isLoading: workflowsLoading,
    hasEverLoaded: workflowsLoaded,
  } = useLibraWorkflows(useMemo(() => ({ ids: workflowIds }), [workflowIds]))

  const workflowMap = useMemo(
    () => new Map(libraWorkflows.map((w) => [w.id, w])),
    [libraWorkflows]
  )

  const filteredTickets = useMemo(() => {
    if (!selectedSiteId) return latestTickets
    return latestTickets.filter((ticket) => ticket.tags.site === selectedSiteId)
  }, [latestTickets, selectedSiteId])

  const filteredAllTickets = useMemo(() => {
    if (!selectedSiteId) return tickets
    return tickets.filter((ticket) => ticket.tags.site === selectedSiteId)
  }, [selectedSiteId, tickets])
  const statsTickets = useMemo(
    () => getLatestTicketPerAssetForStats(filteredAllTickets),
    [filteredAllTickets]
  )

  const siteIds = useMemo(
    () =>
      uniq(
        latestTickets
          .map((ticket) => ticket.tags.site)
          .filter((site): site is string => site !== undefined)
      ),
    [latestTickets]
  )

  const isLoading = ticketsLoading

  const visibleAssetIds = useMemo(() => {
    const allAssetIds = uniq(
      statsTickets
        .map((ticket) => ticket.tags.unit)
        .filter((unit): unit is string => unit !== undefined)
    )

    if (!selectedStatus) return allAssetIds

    return allAssetIds.filter((unitId) => {
      const unitStatsTickets = statsTickets.filter(
        (ticket) => ticket.tags.unit === unitId
      )
      return getAssetStatus(unitStatsTickets, workflowMap) === selectedStatus
    })
  }, [selectedStatus, statsTickets, workflowMap])

  const visibleTickets = useMemo(() => {
    return filteredTickets.filter((ticket) =>
      visibleAssetIds.includes(ticket.tags.unit as string)
    )
  }, [filteredTickets, visibleAssetIds])
  const visibleAllTickets = useMemo(() => {
    return filteredAllTickets.filter((ticket) =>
      visibleAssetIds.includes(ticket.tags.unit as string)
    )
  }, [filteredAllTickets, visibleAssetIds])
  const visibleStatsTickets = useMemo(() => {
    return statsTickets.filter((ticket) =>
      visibleAssetIds.includes(ticket.tags.unit as string)
    )
  }, [statsTickets, visibleAssetIds])

  if (!organizationId) {
    return (
      <ErrorMessage>
        <Alert
          type='info'
          message='Please select an Organization to view NII progress'
          showIcon
        />
      </ErrorMessage>
    )
  }

  return (
    <>
      <ContentWrapper>
        <TopRow>
          <SiteFilterRow
            siteIds={siteIds}
            selectedSiteId={selectedSiteId}
            setSelectedSiteId={setSelectedSiteId}
          />
          <ButtonContainer>
            <CreateTicketButton
              organizationId={organizationId}
              group={group}
              onSuccess={forceRefresh}
            />
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <DeleteTicketButton
                organizationId={organizationId}
                group={group}
                tickets={tickets}
                onSuccess={forceRefresh}
              />
            </PermissionsGate>
          </ButtonContainer>
        </TopRow>

        <ExecutiveDashboard
          tickets={statsTickets}
          isLoading={isLoading}
          totalAssets={group.totalAssets}
          group={group}
          selectedSiteId={selectedSiteId}
          onRefreshGroup={onRefreshGroup}
          siteFilterSiteIds={siteIds}
          roiVisibleUserIds={config.roiVisibleUserIds}
          onHideRoiForMe={
            isGeckoInternal && isOptedIn ? hideRoiForMe : undefined
          }
          hidingRoi={hidingRoi}
        />
        <AssetStatusTracker
          tickets={statsTickets}
          selectedStatus={selectedStatus}
          onStatusSelect={setSelectedStatus}
        />
        {config.ticketDashboard ? (
          <LibraTicketDashboard
            key={groupId}
            dashboardId={config.id}
            settings={config.ticketDashboard}
            tickets={visibleAllTickets}
            workflows={libraWorkflows}
            isLoading={ticketsLoading || (workflowsLoading && !workflowsLoaded)}
            hasCreateVersionPermission={hasCreateVersionPermission}
            onRefresh={forceRefresh}
          />
        ) : (
          <LegacyDashboardViews
            latestTickets={latestTickets}
            tickets={visibleTickets}
            allTickets={visibleAllTickets}
            statsTickets={visibleStatsTickets}
            workflows={libraWorkflows}
            workflowMap={workflowMap}
            isLoading={isLoading}
            onRefresh={forceRefresh}
            canCreateVersion={canCreateVersion}
          />
        )}
      </ContentWrapper>
    </>
  )
}

const CreateTicketButton: FC<{
  organizationId: string | undefined
  group: LibraTicketGroup | undefined
  onSuccess: () => void
}> = ({ organizationId, group, onSuccess }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const isDisabled = !organizationId || !group

  return (
    <>
      <Button
        type='primary'
        icon={<PlusCircleOutlined />}
        onClick={() => setIsCreateModalOpen(true)}
        disabled={isDisabled}>
        Create Tickets
      </Button>
      {organizationId && group && (
        <CreateTicketsForGroupModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={onSuccess}
          organizationId={organizationId}
          groupId={group.id}
          groupTitle={group.title}
          createWorkflowIds={group.createWorkflowIds}
        />
      )}
    </>
  )
}

const DeleteTicketButton: FC<{
  organizationId: string | undefined
  group: LibraTicketGroup | undefined
  tickets: ReadonlyArray<LibraTicket>
  onSuccess: () => void | Promise<void>
}> = ({ organizationId, group, tickets, onSuccess }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const isDisabled = !organizationId || !group || tickets.length === 0
  const disabledReason =
    tickets.length === 0 ? 'There are no tickets to delete' : undefined

  return (
    <>
      <Tooltip title={disabledReason}>
        <span>
          <Button
            type='primary'
            danger
            icon={<DeleteOutlined />}
            onClick={() => setIsDeleteModalOpen(true)}
            disabled={isDisabled}>
            Delete Tickets
          </Button>
        </span>
      </Tooltip>
      {organizationId && group && (
        <DeleteTicketsForGroupModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onSuccess={onSuccess}
          organizationId={organizationId}
          groupId={group.id}
          groupTitle={group.title}
          tickets={tickets}
        />
      )}
    </>
  )
}

const SiteFilterRow: FC<{
  siteIds: ReadonlyArray<string>
  selectedSiteId: string | undefined
  setSelectedSiteId: (siteId: string | undefined) => void
}> = ({ siteIds, selectedSiteId, setSelectedSiteId }) => {
  const { sites: siteEntries, isLoading: sitesLoading } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.SITE,
        ids: [...siteIds],
      }),
      [siteIds]
    )
  )

  const sortedSiteEntries = useMemo(
    () =>
      siteEntries.toSorted((a, b) =>
        (a.data.name ?? '').localeCompare(b.data.name ?? '')
      ),
    [siteEntries]
  )

  if (sitesLoading) {
    return (
      <SiteFiltersContainer>
        <Skeleton active paragraph={{ rows: 1 }} />
      </SiteFiltersContainer>
    )
  }

  if (siteEntries.length === 0) {
    return <SiteFiltersContainer />
  }

  return (
    <SiteFiltersContainer>
      <SiteFiltersLabel>Sites:</SiteFiltersLabel>
      <SitePills>
        <SiteTag
          $isSelected={!selectedSiteId}
          onClick={() => setSelectedSiteId(undefined)}>
          All Sites
        </SiteTag>
        {sortedSiteEntries.map((site) => (
          <SiteTag
            key={site.id}
            $isSelected={selectedSiteId === site.id}
            onClick={() =>
              setSelectedSiteId(
                selectedSiteId === site.id ? undefined : site.id
              )
            }>
            {site.data.name as string}
          </SiteTag>
        ))}
      </SitePills>
    </SiteFiltersContainer>
  )
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  min-height: 0;
  /* Reserving the scrollbar's space stops content reflowing as it appears, which made it toggle on and off. */
  scrollbar-gutter: stable;
`

const ErrorMessage = styled.div`
  padding: 1rem;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`

const SiteFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

const SiteFiltersLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
  white-space: nowrap;
`

const SitePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const SiteTag = styled(Tag)<{ $isSelected: boolean }>`
  cursor: pointer;
  margin: 0;
  padding: 4px 12px;
  border-radius: 16px;
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  border-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? '#fff' : theme.forecast.activeText};

  &:hover {
    background: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[600]};
    border-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[600]};
    color: ${({ theme, $isSelected }) =>
      $isSelected ? '#fff' : theme.forecast.activeText};
  }
`
